import './styles.scss';
import React from 'react';
import track from 'react-tracking';
import PropTypes from 'prop-types';
import { Container } from '@edx/paragon';
import { FormattedMessage, injectIntl } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import Breadcrumbs from '@prospectus/common/ui/Breadcrumbs';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';
import { getTopicUrl } from '@utils/topics';

function SubjectTopics({
  uuid,
  topics,
  title,
  subjects,
  intl,
}) {
  const getTopicLinks = () => {
    const matchedTopics = [];

    // TODO: use GraphQL filter syntax to avoid these nested forEach clauses
    topics.forEach(({ node: { frontmatter } }) => {
      const {
        title: frontmatterTitle,
        pageTitle,
        productAreas,
      } = frontmatter;

      if (productAreas) {
        productAreas.forEach(({ productArea }) => {
          const { productList } = productArea;
          if (productList) {
            productList.forEach((product) => {
              if (product.uuid === uuid) {
                const matchedTopic = {
                  label: pageTitle,
                  url: `topic/${frontmatterTitle.toLowerCase().replace(/\s+/g, '-')}`,
                };
                matchedTopics.push(matchedTopic);
              }
            });
          }
        });
      }
    });

    return matchedTopics;
  };

  const primarySubject = subjects[0];
  const breadcrumbLinks = [{
    label: primarySubject.name,
    href: getTopicUrl(intl.locale, primarySubject.slug),
    subject: primarySubject.slug,
  }];
  const topicLinks = getTopicLinks();

  return (
    <div className="subject-topics mb-5 mt-lg-5">
      <Container size="lg">
        <h2 className="h2 title">
          <FormattedMessage
            id="prospectus.subjectTopics.title"
            defaultMessage="Also in {subjectName} at edX"
            description="Title for the other topics in the current subject."
            values={{ subjectName: primarySubject.name }}
          />
        </h2>
        <Breadcrumbs links={breadcrumbLinks} activeLabel={title} />
        <Container size="lg">
          <div className="row">
            {topicLinks.map(({ url, label }) => (
              <ProspectusLink
                href={url}
                key={url}
                className="badge badge-topic"
                analytics={{
                  linkProps: {
                    category: 'subject',
                    label: `${label}`,
                    featured: false,
                  },
                  eventName: 'edx.bi.subject.topics.breadcrumbs.link.click',
                }}
              >
                {label}
              </ProspectusLink>
            ))}
          </div>
        </Container>
      </Container>
    </div>
  );
}

SubjectTopics.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  subjects: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  })).isRequired,
  uuid: PropTypes.string.isRequired,
  topics: PropTypes.arrayOf(PropTypes.shape({
    node: PropTypes.shape({
      frontmatter: PropTypes.shape({
        title: PropTypes.string.isRequired,
        pageTitle: PropTypes.string.isRequired,
        productAreas: PropTypes.arrayOf(PropTypes.shape({
          productArea: PropTypes.shape({
            productList: PropTypes.arrayOf(PropTypes.shape({
              product: PropTypes.shape({
                uuid: PropTypes.string.isRequired,
              }),
            })),
          }),
        })),
      }),
    }),
  })),
  intl: intlShape.isRequired,
};

SubjectTopics.defaultProps = {
  topics: [],
};

export default track({
  component: 'subject-topics',
})(injectIntl(SubjectTopics));
